/**
 * Coverage calculation utilities
 */

import type {
  Zip3ReferenceMap,
  ZoneMatrix,
  CoverageResult,
  DestinationCoverage,
  WarehouseLocation,
} from '../types';
import { haversineDistance } from '../utils/distance';

interface WarehouseCandidate {
  zip3: string;
  days: number;
  distance: number;
  carrier?: string;
}

/**
 * Find the best warehouse to serve a destination using sophisticated tie-breaking
 */
function findBestWarehouse(
  warehouseZip3s: string[],
  destZip3: string,
  destIdx: number,
  zoneMatrix: ZoneMatrix,
  zip3Reference: Zip3ReferenceMap,
  deliveryGoal: 2 | 3
): WarehouseCandidate {
  const destRef = zip3Reference[destZip3];
  
  if (!destRef) {
    return { zip3: '', days: 99, distance: 9999 };
  }

  const candidates: WarehouseCandidate[] = [];

  // Gather all warehouse candidates with their metrics
  for (const whZip3 of warehouseZip3s) {
    const whIdx = zoneMatrix.origins.indexOf(whZip3);
    if (whIdx === -1) continue;

    const whRef = zip3Reference[whZip3];
    if (!whRef) continue;

    const days = zoneMatrix.matrix[whIdx][destIdx];
    const distance = haversineDistance(
      whRef.lat, whRef.lng,
      destRef.lat, destRef.lng
    );

    // Get carrier if available (from merged matrix or single carrier matrix)
    const carrier = zoneMatrix.carrierMap 
      ? zoneMatrix.carrierMap[whIdx][destIdx]
      : zoneMatrix.carrier;

    candidates.push({ zip3: whZip3, days, distance, carrier });
  }

  if (candidates.length === 0) {
    return { zip3: '', days: 99, distance: 9999, carrier: undefined };
  }

  // Sort by sophisticated tie-breaking rules
  candidates.sort((a, b) => {
    // 1. Primary: Shortest delivery time (lower is better)
    if (a.days !== b.days) {
      return a.days - b.days;
    }

    // TIED at delivery time - apply hierarchy:

    // Get actual delivery capabilities for tie-breaking
    const aIdx = zoneMatrix.origins.indexOf(a.zip3);
    const bIdx = zoneMatrix.origins.indexOf(b.zip3);
    const aDays = zoneMatrix.matrix[aIdx][destIdx];
    const bDays = zoneMatrix.matrix[bIdx][destIdx];

    // 2. If target is 3-day and we're tied at 3, prefer 2-day capability
    if (deliveryGoal === 3 && a.days === 3) {
      const a_can2day = aDays <= 2;
      const b_can2day = bDays <= 2;
      
      if (a_can2day !== b_can2day) {
        return a_can2day ? -1 : 1; // 2-day capability wins
      }
    }

    // 3. Prefer 1-day capability (express infrastructure indicator)
    const a_can1day = aDays <= 1;
    const b_can1day = bDays <= 1;
    
    if (a_can1day !== b_can1day) {
      return a_can1day ? -1 : 1; // 1-day capability wins
    }

    // 4. Prefer closer distance (0.1 mile tolerance to avoid floating point issues)
    if (Math.abs(a.distance - b.distance) > 0.1) {
      return a.distance - b.distance;
    }

    // 5. Alphabetical fallback (deterministic)
    return a.zip3.localeCompare(b.zip3);
  });

  return candidates[0];
}

/**
 * Calculate delivery coverage from specified warehouse locations
 */
export function calculateCoverage(
  warehouseZip3s: string[],
  zoneMatrix: ZoneMatrix,
  zip3Reference: Zip3ReferenceMap,
  deliveryGoal: 2 | 3 = 2
): CoverageResult {
  if (warehouseZip3s.length === 0) {
    return {
      warehouses: [],
      totalPopulation: 0,
      population2Day: 0,
      population3Day: 0,
      pct2DayCoverage: 0,
      pct3DayCoverage: 0,
      avgDeliveryDays: 0,
      coverageByDest: [],
    };
  }

  // Calculate best transit time for each destination
  const coverageByDest: DestinationCoverage[] = [];
  let totalPopulation = 0;
  let population2Day = 0;
  let population3Day = 0;
  let totalDaysWeighted = 0;

  for (let destIdx = 0; destIdx < zoneMatrix.destinations.length; destIdx++) {
    const destZip3 = zoneMatrix.destinations[destIdx];
    const destData = zip3Reference[destZip3];

    if (!destData) continue;

    // Find best warehouse using sophisticated tie-breaking
    const bestWarehouse = findBestWarehouse(
      warehouseZip3s,
      destZip3,
      destIdx,
      zoneMatrix,
      zip3Reference,
      deliveryGoal
    );

    const bestDays = bestWarehouse.days === 99 ? 5 : bestWarehouse.days;

    const population = destData.population;
    const is2Day = bestDays <= 2;
    const is3Day = bestDays <= 3;

    coverageByDest.push({
      zip3: destZip3,
      transit_days: bestDays,
      carrier: bestWarehouse.carrier,
      population,
      lat: destData.lat,
      lng: destData.lng,
      city: destData.city,
      state: destData.state,
      is_2day: is2Day,
      is_3day: is3Day,
    });

    totalPopulation += population;
    if (is2Day) population2Day += population;
    if (is3Day) population3Day += population;
    totalDaysWeighted += bestDays * population;
  }

  // Calculate percentages
  const pct2DayCoverage = totalPopulation > 0
    ? Math.round((population2Day / totalPopulation) * 10000) / 100
    : 0;

  const pct3DayCoverage = totalPopulation > 0
    ? Math.round((population3Day / totalPopulation) * 10000) / 100
    : 0;

  const avgDeliveryDays = totalPopulation > 0
    ? Math.round((totalDaysWeighted / totalPopulation) * 100) / 100
    : 0;

  // Build warehouse details
  const warehouses: WarehouseLocation[] = warehouseZip3s.map(zip3 => ({
    zip3,
    lat: zip3Reference[zip3]?.lat,
    lng: zip3Reference[zip3]?.lng,
    city: zip3Reference[zip3]?.city,
    state: zip3Reference[zip3]?.state,
  }));

  return {
    warehouses,
    totalPopulation,
    population2Day,
    population3Day,
    pct2DayCoverage,
    pct3DayCoverage,
    avgDeliveryDays,
    coverageByDest,
  };
}

/**
 * Recalculate per-warehouse coverage ownership
 * Each warehouse gets credit for destinations where it provides the best delivery time
 */
export function recalculateWarehouseOwnership(
  warehouses: WarehouseLocation[],
  zoneMatrix: ZoneMatrix,
  zip3Reference: Zip3ReferenceMap,
  deliveryGoal: 2 | 3 = 2
): WarehouseLocation[] {
  const warehouseZip3s = warehouses.map(w => w.zip3);
  
  return warehouses.map(wh => {
    let warehouseCredit = 0;

    for (let destIdx = 0; destIdx < zoneMatrix.destinations.length; destIdx++) {
      const dest = zoneMatrix.destinations[destIdx];
      
      const bestWarehouse = findBestWarehouse(
        warehouseZip3s,
        dest,
        destIdx,
        zoneMatrix,
        zip3Reference,
        deliveryGoal
      );

      // Credit this warehouse if it won AND meets delivery goal
      if (bestWarehouse.zip3 === wh.zip3 && bestWarehouse.days <= deliveryGoal) {
        warehouseCredit += zip3Reference[dest]?.population ?? 0;
      }
    }

    return {
      ...wh,
      populationCovered: warehouseCredit,
    };
  });
}

/**
 * Validate warehouse ZIP3s exist in reference data
 */
export function validateWarehouseZip3s(
  zip3s: string[],
  zip3Reference: Zip3ReferenceMap
): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];

  for (const zip3 of zip3s) {
    if (zip3Reference[zip3]) {
      valid.push(zip3);
    } else {
      invalid.push(zip3);
    }
  }

  return { valid, invalid };
}

